import * as React from "react";

import { cn } from "./cn";

type ReportGridRowKind = "section" | "group" | "account" | "total";

type ReportGridRow = {
  id: string;
  kind: ReportGridRowKind;
  /** Indent level: 12px per level. */
  depth: number;
  label: string;
  values: Record<string, number | null>;
  total?: number | null;
  collapsible?: boolean;
  parentId?: string | null;
};

type ReportGridColumn = {
  key: string;
  label: string;
  /** A period still in progress (the header already says so, e.g. "MTD"). */
  partial?: boolean;
};

type RolledUp = { values: Record<string, number | null>; total: number | null };

function childrenByParent(rows: readonly ReportGridRow[]) {
  const map = new Map<string, ReportGridRow[]>();
  for (const row of rows) {
    if (row.parentId == null) continue;
    const list = map.get(row.parentId) ?? [];
    list.push(row);
    map.set(row.parentId, list);
  }
  return map;
}

/** Rows left standing once every collapsed row's descendants are hidden. */
function visibleReportRows(
  rows: readonly ReportGridRow[],
  collapsed: ReadonlySet<string>,
): ReportGridRow[] {
  if (collapsed.size === 0) return [...rows];
  const byId = new Map(rows.map((row) => [row.id, row]));
  const hidden = (row: ReportGridRow) => {
    const seen = new Set<string>();
    let parent = row.parentId == null ? undefined : byId.get(row.parentId);
    while (parent && !seen.has(parent.id)) {
      if (collapsed.has(parent.id)) return true;
      seen.add(parent.id);
      parent = parent.parentId == null ? undefined : byId.get(parent.parentId);
    }
    return false;
  };
  return rows.filter((row) => !hidden(row));
}

/** Rows that can fold: marked collapsible (or unmarked), with children, never totals. */
function collapsibleRowIds(rows: readonly ReportGridRow[]): string[] {
  const children = childrenByParent(rows);
  return rows
    .filter(
      (row) =>
        row.kind !== "total" &&
        row.collapsible !== false &&
        (row.collapsible === true || row.kind === "group") &&
        (children.get(row.id)?.length ?? 0) > 0,
    )
    .map((row) => row.id);
}

function descendants(rows: readonly ReportGridRow[], rowId: string) {
  const children = childrenByParent(rows);
  const out: ReportGridRow[] = [];
  const seen = new Set<string>([rowId]);
  const queue = [...(children.get(rowId) ?? [])];
  while (queue.length > 0) {
    const row = queue.shift() as ReportGridRow;
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    out.push(row);
    queue.push(...(children.get(row.id) ?? []));
  }
  return out;
}

/**
 * What a collapsed row shows in its own cells: its matching `total` child
 * when one exists, else the sum of its descendant account rows.
 */
function rolledUpValues(
  rows: readonly ReportGridRow[],
  rowId: string,
): RolledUp {
  const below = descendants(rows, rowId);
  const own = below.find(
    (row) => row.kind === "total" && row.parentId === rowId,
  );
  if (own) return { values: { ...own.values }, total: own.total ?? null };
  const values: Record<string, number | null> = {};
  let total: number | null = null;
  for (const row of below) {
    if (row.kind !== "account") continue;
    for (const [key, value] of Object.entries(row.values)) {
      if (value == null) continue;
      values[key] = (values[key] ?? 0) + value;
    }
    if (row.total != null) total = (total ?? 0) + row.total;
  }
  return { values, total };
}

const ROW_HEIGHT = "h-9";
const HEADER_CELL =
  "sticky top-0 z-20 h-9 border-b border-border bg-card px-cell-x py-0 text-right text-compact font-medium whitespace-nowrap text-muted-foreground";
const TINT = "bg-muted/60";

function Caret({ open }: { open: boolean }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      className={cn(
        "size-4 shrink-0 transition-transform motion-reduce:transition-none",
        open ? "" : "-rotate-90",
      )}
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m4 6 4 4 4-4" />
    </svg>
  );
}

type ReportGridProps = {
  caption: string;
  columns: ReportGridColumn[];
  totalColumn?: { key: string; label: string } | null;
  rows: ReportGridRow[];
  formatValue: (minor: number | null | undefined) => string;
  collapsed?: ReadonlySet<string>;
  defaultCollapsed?: Iterable<string>;
  onCollapsedChange?: (next: Set<string>) => void;
  highlightColumn?: string | null;
  onHighlightColumn?: (key: string | null) => void;
  renderLabel?: (row: ReportGridRow) => React.ReactNode;
  firstColumnLabel?: string;
  className?: string;
};

/**
 * A financial report: a real table in its own horizontal scroller, with the
 * header, the label column and the total column pinned. Rows fold by parent
 * chain; hovering a cell tints its whole column.
 */
function ReportGrid({
  caption,
  columns,
  totalColumn = null,
  rows,
  formatValue,
  collapsed,
  defaultCollapsed,
  onCollapsedChange,
  highlightColumn = null,
  onHighlightColumn,
  renderLabel,
  firstColumnLabel = "",
  className,
}: ReportGridProps) {
  const [inner, setInner] = React.useState<Set<string>>(
    () => new Set(defaultCollapsed ?? []),
  );
  const [hover, setHover] = React.useState<string | null>(null);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const open = collapsed ?? inner;
  const active = highlightColumn ?? hover;
  const foldable = React.useMemo(
    () => new Set(collapsibleRowIds(rows)),
    [rows],
  );
  const visible = React.useMemo(
    () => visibleReportRows(rows, open),
    [rows, open],
  );

  const toggle = (id: string) => {
    const next = new Set(open);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    if (collapsed === undefined) setInner(next);
    onCollapsedChange?.(next);
  };

  const hoverColumn = (key: string | null) => {
    setHover(key);
    onHighlightColumn?.(key);
  };

  React.useEffect(() => {
    if (!highlightColumn) return;
    const header = Array.from(
      wrapRef.current?.querySelectorAll<HTMLElement>("th[data-column]") ?? [],
    ).find((th) => th.dataset.column === highlightColumn);
    header?.scrollIntoView?.({ inline: "nearest", block: "nearest" });
  }, [highlightColumn]);

  const columnProps = (key: string) => ({
    "data-column": key,
    "data-highlighted": active === key ? "true" : undefined,
    onPointerEnter: () => hoverColumn(key),
  });

  return (
    <div
      ref={wrapRef}
      data-slot="report-grid"
      className={cn(
        "max-h-[75dvh] max-w-full overflow-x-auto overflow-y-auto rounded-card bg-card ring-1 ring-border",
        className,
      )}
    >
      <table
        className="w-full border-separate border-spacing-0 text-compact"
        onPointerLeave={() => hoverColumn(null)}
      >
        <caption className="sr-only">{caption}</caption>
        <thead>
          <tr>
            <th
              scope="col"
              className={cn(
                HEADER_CELL,
                "left-0 z-30 min-w-40 sm:min-w-56 border-r text-left",
              )}
            >
              {firstColumnLabel}
            </th>
            {columns.map((column) => (
              <th
                key={column.key}
                scope="col"
                {...columnProps(column.key)}
                data-partial={column.partial ? "true" : undefined}
                className={cn(
                  HEADER_CELL,
                  "min-w-30",
                  active === column.key && TINT,
                )}
              >
                {column.label}
              </th>
            ))}
            {totalColumn ? (
              <th
                scope="col"
                {...columnProps(totalColumn.key)}
                className={cn(
                  HEADER_CELL,
                  "right-0 z-30 min-w-30 border-l text-foreground",
                )}
              >
                {totalColumn.label}
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {visible.map((row) => {
            const isTotal = row.kind === "total";
            const isSection = row.kind === "section";
            const canFold = foldable.has(row.id);
            const isCollapsed = canFold && open.has(row.id);
            const shown: RolledUp | null = isCollapsed
              ? rolledUpValues(rows, row.id)
              : isSection
                ? null
                : { values: row.values, total: row.total ?? null };
            const fill = isTotal ? "bg-muted" : "bg-card";
            return (
              <tr
                key={row.id}
                data-kind={row.kind}
                className={cn(
                  ROW_HEIGHT,
                  isTotal
                    ? "font-semibold text-foreground"
                    : isSection || row.kind === "group"
                      ? "font-medium text-foreground"
                      : "text-body",
                )}
              >
                <th
                  scope="row"
                  className={cn(
                    "sticky left-0 z-10 h-9 min-w-40 sm:min-w-56 border-r border-b border-border px-cell-x py-0 text-left [font-weight:inherit]",
                    fill,
                  )}
                >
                  <div className="flex items-center gap-1">
                    <span className="inline-flex size-5 shrink-0 items-center justify-center">
                      {canFold ? (
                        <button
                          type="button"
                          aria-expanded={!isCollapsed}
                          aria-label={`${isCollapsed ? "Expand" : "Collapse"} ${row.label}`}
                          onClick={() => toggle(row.id)}
                          className="inline-flex size-5 items-center justify-center rounded-sm text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-ring"
                        >
                          <Caret open={!isCollapsed} />
                        </button>
                      ) : null}
                    </span>
                    <span
                      className="min-w-0 truncate"
                      style={{ paddingLeft: row.depth * 12 }}
                    >
                      {renderLabel ? renderLabel(row) : row.label}
                    </span>
                  </div>
                </th>
                {columns.map((column) => (
                  <td
                    key={column.key}
                    {...columnProps(column.key)}
                    className={cn(
                      "type-figure h-9 border-b border-border px-cell-x py-0 text-right whitespace-nowrap",
                      isTotal && "bg-muted",
                      active === column.key && !isTotal && TINT,
                    )}
                  >
                    {shown ? formatValue(shown.values[column.key]) : ""}
                  </td>
                ))}
                {totalColumn ? (
                  <td
                    {...columnProps(totalColumn.key)}
                    className={cn(
                      "type-figure sticky right-0 z-10 h-9 border-b border-l border-border px-cell-x py-0 text-right font-medium whitespace-nowrap text-foreground",
                      fill,
                    )}
                  >
                    {shown ? formatValue(shown.total) : ""}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** The grid's loading shape: the same row height, so nothing shifts on load. */
function ReportGridSkeleton({
  columns,
  rows = 8,
}: {
  columns: number;
  rows?: number;
}) {
  return (
    <div
      aria-hidden="true"
      data-slot="report-grid-skeleton"
      className="max-w-full overflow-x-auto rounded-card bg-card ring-1 ring-border"
    >
      <table className="w-full border-separate border-spacing-0">
        <thead>
          <tr>
            {Array.from({ length: columns + 1 }, (_, i) => (
              <th
                key={i}
                className="h-9 border-b border-border px-cell-x py-0"
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }, (_, row) => (
            <tr key={row} className={ROW_HEIGHT}>
              {Array.from({ length: columns + 1 }, (_, i) => (
                <td
                  key={i}
                  className="h-9 border-b border-border px-cell-x py-0"
                >
                  <div
                    className={cn(
                      "h-3 animate-pulse rounded-sm bg-line motion-reduce:animate-none",
                      i === 0 ? "w-32" : "ml-auto w-16",
                    )}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export {
  collapsibleRowIds,
  ReportGrid,
  ReportGridSkeleton,
  rolledUpValues,
  visibleReportRows,
};
export type { ReportGridColumn, ReportGridProps, ReportGridRow, RolledUp };
